import React, { useState, useRef, useEffect } from "react";
import "./selectPicker.css"; // Ensure proper styles are in place

const SelectPicker = ({ 
  options, 
  label, 
  value, 
  handleChange, 
  readOnly, 
  required = false, 
  error, 
  touched, 
  setFieldTouched,  // Ensure setFieldTouched is passed from Formik
  name // Ensure field name is passed for ErrorMessage
}) => {
  const [searchTerm, setSearchTerm] = useState(value?.label || "");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const dropdownRef = useRef(null);

  const getFilteredOptions = () => {
    let matchingOptions = options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    let nonMatchingOptions = options.filter(option =>
      !option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (value && searchTerm && value.label.toLowerCase().includes(searchTerm.toLowerCase())) {
      matchingOptions = [value, ...matchingOptions.filter(opt => opt.value !== value.value)];
    }

    return [...matchingOptions, ...nonMatchingOptions];
  };

  const filteredOptions = getFilteredOptions();

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
  
    if (newValue === "") {
      handleChange(null); // Reset selection when input is cleared
    }
  };

  const handleSelect = (option) => {
    handleChange(option);
    setSearchTerm(option.label); // Set input value
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        setHighlightIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
        break;
      case "ArrowUp":
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        if (highlightIndex >= 0 && filteredOptions[highlightIndex]) {
          handleSelect(filteredOptions[highlightIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`selectpicker-container ${isOpen ? "active" : ""}`} ref={dropdownRef}>

      <div
        className={`selectpicker-dropdown-container ${error && touched ? "error-border" : ""}`}
        tabIndex="0"
        onKeyDown={handleKeyDown}
        onBlur={() => setFieldTouched(name, true)} // Trigger touch state for validation
      >
        <label className={`floating-placeholder ${searchTerm ? "active" : ""}`}>
          {label}
          {required && <span className="required-asterisk"> *</span>}
        </label>


        {/* Your actual input */}
        <input
          type="text"
          className="selectpicker-dropdown"
          value={searchTerm}
          onChange={handleInputChange}
          disabled={readOnly}
          onClick={() => setIsOpen(true)} 
          autoComplete="new-password" // More reliable than "off" so selectpicker doesnt get autofilled
        />
      </div>

      {isOpen && (
        <div className="selectpicker-options-container">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`selectpicker-option 
                  ${index === highlightIndex ? "highlighted" : ""} 
                  ${option.value === value?.value ? "selected" : ""}`}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightIndex(index)}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className="selectpicker-option">No results found</div>
          )}
        </div>
      )}

    </div>
  );
};


export default SelectPicker;